import { forwardRef, InputHTMLAttributes, useId } from 'react';

import { cn } from '../utils/utils';

type Props = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  hint?: string;
  error?: string;
  className?: string;
};

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    { label, type = 'text', placeholder, value, onChange, name, hint, error, className, ...props },
    ref
  ) => {
    const id = useId();
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
    const requiredIndicator = '*';
    return (
      <div>
        {label && (
          <label htmlFor={id} className="text-sm font-medium text-(--color-textDefault)">
            {label}
            {props.required && <span className="ml-1 text-red-600" aria-label="required">{requiredIndicator}</span>}
          </label>
        )}
        <input
          ref={ref}
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          aria-describedby={describedBy}
          aria-invalid={!!error}
          aria-required={props.required}
          className={cn('rm-input', className, { error: error })}
          {...props}
          id={id}
        />
        {hint && (
          <p id={hintId} className="text-xs text-(--color-textSubtle)" style={{}}>
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} className="pt-1 text-xs text-(--color-iconAccentRedDefault)">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
